import { useState } from "react";
import { toast } from "sonner";
import { Baby, Briefcase, Building, Heart, Home, Key, Landmark, User, Users } from "lucide-react";
import { Chip, Field, HOUSING, PRIORITIES } from "@/components/onboarding/parts";
import { api, formatApiError } from "@/lib/api";

const DemoBankButton = () => {
  const [done, setDone] = useState(false);
  const go = async () => {
    try { await api.post("/bridge/simulate"); setDone(true); toast.success("Banque de démonstration connectée : comptes et mouvements fictifs ajoutés"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return done ? <p className="mt-3 text-sm font-bold text-success" data-testid="setup-demo-bank-done">Banque de démonstration connectée. Vous pouvez passer à la suite.</p>
    : <button type="button" onClick={go} className="mt-3 text-sm font-bold text-primary underline-offset-4 hover:underline" data-testid="setup-demo-bank">Essayer d'abord avec une banque de démonstration (données fictives)</button>;
};

const n = (x) => (x === "" || x == null ? null : Number(x));
const Grid = ({ children }) => <div className="grid gap-2 sm:grid-cols-2">{children}</div>;
export const BUDGET_CATS = [["alimentation", "Alimentation"], ["logement", "Logement"], ["energie", "Énergie"], ["transport", "Transport"], ["loisirs", "Loisirs"], ["abonnements", "Abonnements"]];

const Situation = ({ v, set }) => (
  <div className="space-y-5">
    <Grid>{[["single", "Seul(e)", User], ["couple", "En couple", Heart], ["family", "En famille", Users], ["shared", "En colocation", Home]].map(([k, l, I]) => (
      <Chip key={k} icon={I} label={l} active={v.status === k} onClick={() => set({ status: k })} testid={`setup-situation-${k}`} />))}</Grid>
    <div className="grid grid-cols-2 gap-3">
      <Field id="s-adults" label="Adultes" type="number" min="1" value={v.adults ?? ""} onChange={(e) => set({ adults: e.target.value })} data-testid="setup-adults-input" />
      <Field id="s-children" label="Enfants" type="number" min="0" value={v.children ?? ""} onChange={(e) => set({ children: e.target.value })} data-testid="setup-children-input" />
    </div>
  </div>
);

const Housing = ({ v, set }) => (
  <div className="space-y-5">
    <Grid>{HOUSING.map((h) => <Chip key={h.key} {...h} active={v.housing_type === h.key} onClick={() => set({ housing_type: h.key })} testid={`setup-housing-${h.key}`} />)}</Grid>
    <Grid>{[["owner", "Propriétaire", Key], ["tenant", "Locataire", Building]].map(([k, l, I]) => <Chip key={k} icon={I} label={l} active={v.tenure === k} onClick={() => set({ tenure: k })} testid={`setup-tenure-${k}`} />)}</Grid>
    <div className="grid grid-cols-2 gap-3">
      <Field id="s-m2" label="Surface (m²)" type="number" min="1" value={v.surface_m2 ?? ""} onChange={(e) => set({ surface_m2: e.target.value })} data-testid="setup-surface-input" />
      <Field id="s-rooms" label="Nombre de pièces" type="number" min="1" value={v.rooms_count ?? ""} onChange={(e) => set({ rooms_count: e.target.value })} data-testid="setup-rooms-input" />
    </div>
  </div>
);

const Income = ({ v, set }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    <Field id="s-inc" label="Revenus nets du foyer par mois (€)" type="number" min="0" value={v.income ?? ""} onChange={(e) => set({ income: e.target.value })} data-testid="setup-income-input" />
    <Field id="s-other" label="Autres revenus par mois (€, facultatif)" type="number" min="0" value={v.other ?? ""} onChange={(e) => set({ other: e.target.value })} data-testid="setup-other-income-input" />
  </div>
);

const Priorities = ({ v, set }) => {
  const list = v.list || [];
  return <Grid>{PRIORITIES.map((p) => <Chip key={p.key} {...p} active={list.includes(p.key)} onClick={() => set({ list: list.includes(p.key) ? list.filter((x) => x !== p.key) : [...list, p.key] })} testid={`setup-priority-${p.key}`} />)}</Grid>;
};

const Budgets = ({ v, set }) => (
  <div className="grid grid-cols-2 gap-3">{BUDGET_CATS.map(([k, l]) => (
    <Field key={k} id={`s-b-${k}`} label={`${l} (€/mois)`} type="number" min="0" value={v[k] ?? ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={`setup-budget-${k}`} />))}</div>
);

const Goal = ({ v, set }) => (
  <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
    <Field id="s-goal" label="Projet" value={v.name ?? ""} onChange={(e) => set({ name: e.target.value })} placeholder="Ex. Vacances d'été" data-testid="setup-goal-name-input" />
    <Field id="s-goal-t" label="Montant (€)" type="number" min="1" value={v.target ?? ""} onChange={(e) => set({ target: e.target.value })} data-testid="setup-goal-target-input" />
  </div>
);

export const BankIntro = ({ demo, label }) => (
  <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5">
    <Landmark className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
    <div className="text-sm"><b>{label}</b><p className="mt-1 text-muted-foreground">{demo
      ? "Compte de démonstration : la connexion est SIMULÉE avec des opérations fictives (aucune vraie banque n'est reliée à un compte partagé)."
      : "Vous allez être redirigé vers notre partenaire d'agrégation bancaire, agréé par l'ACPR (Banque de France) et conforme à la directive européenne DSP2, pour choisir votre banque. L'accès est en lecture seule : CASHMY consulte vos soldes et opérations, mais ne peut faire aucun virement ni paiement. Vos identifiants bancaires ne sont jamais vus ni stockés par CASHMY."}</p>
      <p className="mt-2 text-xs text-muted-foreground">Cliquez sur « Connecter » pour lancer la connexion, ou « Plus tard ».</p>
      {!demo && <DemoBankButton />}</div>
  </div>
);

export const PERSONAL = {
  situation: { title: "Votre situation", text: "Pour adapter les repères et le budget.", Comp: Situation, icon: Baby },
  housing: { title: "Votre logement", text: "Utile pour comparer vos assurances et vos consommations.", Comp: Housing,
    apply: async (v, { hid }) => { await api.patch(`/households/${hid}`, { housing_type: v.housing_type || null, surface_m2: n(v.surface_m2) }); } },
  income: { title: "Vos revenus", text: "Pour calculer votre reste à vivre et vos capacités d'épargne.", Comp: Income },
  priorities: { title: "Vos priorités", text: "Nous organisons l'accueil autour de ce qui compte pour vous.", Comp: Priorities },
  budgets: { title: "Votre budget mensuel", text: "Un plafond par grand poste. Laissez vide ce que vous ne voulez pas suivre.", Comp: Budgets,
    apply: async (v) => { await Promise.all(BUDGET_CATS.filter(([k]) => n(v[k]) > 0).map(([k]) => api.put(`/finance/budgets/${k}`, { limit: n(v[k]) }))); } },
  goal: { title: "Un objectif d'épargne", text: "Facultatif : un projet à financer.", Comp: Goal,
    apply: async (v, { hid, answers }) => { if (!answers.goal && (v.name || "").trim().length >= 2 && n(v.target) > 0) await api.post(`/households/${hid}/goals`, { name: v.name.trim(), target: n(v.target) }); } },
  bank: { title: "Connecter votre banque", text: "Vos opérations arrivent automatiquement, catégorisées.", Comp: ({ demo }) => <BankIntro demo={demo} label="Connexion bancaire sécurisée · lecture seule" />, bank: true,
    connect: async (demo) => {
      if (demo) { await api.post("/bridge/simulate"); return null; }
      const { data } = await api.post("/bridge/connect"); return data.url;
    } },
};
export const BIZ_ICON = Briefcase;
